import React from "react";
import OperationMap from "./OperationMap";
import { SERVICE_DIRECTIONS } from "../import/intent";

const plural = (count, one, many) => `${count} ${count === 1 ? one : many || `${one}s`}`;

const legLine = (leg) => {
  if (!leg) return "";
  const time = leg.startTime ? `${leg.startTime}${leg.endTime ? ` → ${leg.endTime}` : ""}` : "";
  return [time, leg.plate, leg.driver].filter(Boolean).join(" · ");
};

const ServiceRow = ({ group }) => {
  const fill = Math.min(100, Math.round((group.passengerCount / (group.capacity || 15)) * 100));
  const names = group.passengers.map((passenger) => passenger.name);
  const shown = names.slice(0, 6);
  const fromFile = legLine(group.leg);
  return (
    <li className={`start-service${group.overCapacity ? " is-over" : ""}`}>
      <div className="start-service__head">
        <h4>{group.name}</h4>
        <span className={`start-service__count${group.overCapacity ? " is-over" : ""}`}>
          {plural(group.passengerCount, "rider")}
          {group.overCapacity ? ` · over ${group.capacity}` : ""}
        </span>
      </div>
      <div className="start-service__bar" aria-hidden="true">
        <span style={{ width: `${fill}%` }} />
      </div>
      <p className="start-service__meta">
        {group.named ? (fromFile ? `From the file · ${fromFile}` : "Named in the file") : "Grouped by school and capacity"}
        {!fromFile && group.school ? ` · ${group.school}` : ""}
      </p>
      <p className="start-service__names">
        {shown.join(", ")}
        {names.length > shown.length ? `, +${names.length - shown.length} more` : ""}
      </p>
    </li>
  );
};

const DirectionSection = ({ title, hint, groups }) => {
  if (!groups.length) return null;
  const riders = groups.reduce((sum, group) => sum + group.passengerCount, 0);
  return (
    <section className="start-services">
      <header className="start-services__head">
        <h3>{title}</h3>
        <span>
          {plural(groups.length, "service")} · {plural(riders, "rider")}
        </span>
      </header>
      {hint && <p className="start-muted">{hint}</p>}
      <ul className="start-services__list">
        {groups.map((group) => (
          <ServiceRow key={group.id} group={group} />
        ))}
      </ul>
    </section>
  );
};

const StageVisualize = ({
  source,
  preview,
  operation,
  servicePlan,
  serviceDirection,
  maxPassengers,
  selectedRouteId,
  onSelectRoute,
  existingRouteCount,
  namedServiceCount = 0,
  onNext,
  onBack,
}) => {
  const isSample = source === "sample" && operation?.routes?.length;
  const isUpload = source === "upload";
  const pickup = servicePlan?.pickup || [];
  const dropoff = servicePlan?.dropoff || [];
  const total = pickup.length + dropoff.length;
  const named = namedServiceCount > 0;
  const directionLabel =
    serviceDirection === SERVICE_DIRECTIONS.PICKUP
      ? "morning pick-up"
      : serviceDirection === SERVICE_DIRECTIONS.DROPOFF
        ? "afternoon drop-off"
        : "morning and afternoon";

  return (
    <section className="start-stage">
      <header className="start-stage__header">
        <p className="start-kicker">{isSample ? "Demonstration routes" : "Service preview"}</p>
        <h2>
          {isSample
            ? "The sample roster becomes a morning operation"
            : `${plural(total, "service")} will be created`}
        </h2>
        <p>
          {isSample
            ? "These routes are illustrative sample results, not a live optimizer run. They exist so you can see dispatcher, driver, and parent views of the same operation."
            : named
              ? `${directionLabel[0].toUpperCase()}${directionLabel.slice(1)}, exactly as your file defines them — same names, times and vehicles. Only passengers without a service name are grouped by capacity.`
              : `${directionLabel[0].toUpperCase()}${directionLabel.slice(1)}, grouped by school with at most ${maxPassengers || 15} riders per vehicle. Addresses are placed on the map when the services are created.`}
        </p>
      </header>

      {existingRouteCount > 0 && isUpload && (
        <p className="start-note">
          Your account already has {plural(existingRouteCount, "service")}.
          {named
            ? " A service with the same name is reused and the new passengers join it; nothing is replaced."
            : " New services are added beside them; nothing is replaced."}
        </p>
      )}

      {!isSample && [...pickup, ...dropoff].some((group) => group.overCapacity) && (
        <p className="start-note start-note--warn">
          Some services in the file carry more riders than the {maxPassengers || 15} you set. They are kept whole, as the file has them — check the
          vehicle before the first run.
        </p>
      )}

      {isSample ? (
        <OperationMap operation={operation} selectedRouteId={selectedRouteId} onSelectRoute={onSelectRoute} />
      ) : (
        <>
          <DirectionSection title="Morning pick-up" hint="Home → school. Runs in the morning." groups={pickup} />
          <DirectionSection title="Afternoon drop-off" hint="School → home. Runs in the afternoon." groups={dropoff} />
          {!total && (
            <div className="start-schematic">
              {(preview?.schools || []).map((school) => (
                <article key={school.name}>
                  <header>
                    <h3>{school.name}</h3>
                    <span>{school.count} students</span>
                  </header>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      <div className="start-stage__nav">
        <button type="button" className="start-btn start-btn--ghost" onClick={onBack}>
          Back
        </button>
        <button type="button" className="start-btn" onClick={onNext}>
          {isUpload ? "Continue" : "Experience the apps"}
        </button>
      </div>
    </section>
  );
};

export default StageVisualize;
